import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/shared/material.module';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { SnackBarService } from 'src/app/shared/services/snack-service.service';
import { mapTemplateError } from '../template-error';
import { LinkPreview, WorkClassificationTemplate } from '../work-classification-template.models';
import { WorkClassificationTemplateService } from '../work-classification-template.service';

export interface LinkTemplateDialogData {
  projectId: number;
}

export interface LinkTemplateDialogResult {
  templateId: number;
  templateName: string;
  linked: number;
}

/**
 * "Liitä malliin": ties an existing tree to a template by name match. Link only writes provenance ids;
 * it never renames, adds or deletes. Shows the match preview first.
 */
@Component({
  selector: 'app-link-template-dialog',
  templateUrl: './link-template-dialog.component.html',
  styleUrls: ['./link-template-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MaterialModule, TranslatePipe],
})
export class LinkTemplateDialogComponent implements OnInit {
  private data = inject<LinkTemplateDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<LinkTemplateDialogComponent, LinkTemplateDialogResult>>(MatDialogRef);
  private service = inject(WorkClassificationTemplateService);
  private snackBar = inject(SnackBarService);
  private translate = inject(TranslateService);
  private logger = inject(LoggerService);
  private destroyRef = inject(DestroyRef);

  templates = signal<WorkClassificationTemplate[]>([]);
  templatesLoading = signal(true);
  loadFailed = signal(false);
  selectedTemplateId = signal<number | null>(null);

  preview = signal<LinkPreview | null>(null);
  previewLoading = signal(false);
  /** Project node ids that will be linked. All matches are ticked by default. */
  selectedNodeIds = signal<ReadonlySet<number>>(new Set());
  saving = signal(false);

  selectedTemplate = computed(() => this.templates().find(t => t.id === this.selectedTemplateId()) ?? null);
  matchCount = computed(() => this.preview()?.matches.length ?? 0);
  selectedCount = computed(() => this.selectedNodeIds().size);
  allSelected = computed(() => this.matchCount() > 0 && this.selectedCount() === this.matchCount());
  canLink = computed(() => this.selectedCount() > 0 && !this.saving() && !this.previewLoading());

  ngOnInit(): void {
    this.loadTemplates();
  }

  private loadTemplates(): void {
    this.templatesLoading.set(true);
    this.service
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: list => {
          this.templates.set(list);
          this.templatesLoading.set(false);
          this.selectTemplate(list.find(t => t.isDefault)?.id ?? list[0]?.id ?? null);
        },
        error: err => {
          this.templatesLoading.set(false);
          this.loadFailed.set(true);
          this.logger.errorWithContext('LinkTemplateDialog', 'Failed to load templates', err);
        },
      });
  }

  selectTemplate(id: number | null): void {
    this.selectedTemplateId.set(id);
    this.preview.set(null);
    this.selectedNodeIds.set(new Set());
    if (id == null) {
      return;
    }
    this.previewLoading.set(true);
    this.service
      .linkPreview(id, this.data.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: preview => {
          if (this.selectedTemplateId() !== id) {
            return;
          }
          this.preview.set(preview);
          this.selectedNodeIds.set(new Set(preview.matches.map(m => m.projectNodeId)));
          this.previewLoading.set(false);
        },
        error: err => {
          this.previewLoading.set(false);
          this.showError(err);
          if (mapTemplateError(err).code === 'TEMPLATE_INACTIVE') {
            this.loadTemplates();
          }
        },
      });
  }

  isSelected(projectNodeId: number): boolean {
    return this.selectedNodeIds().has(projectNodeId);
  }

  toggle(projectNodeId: number, checked: boolean): void {
    const next = new Set(this.selectedNodeIds());
    if (checked) {
      next.add(projectNodeId);
    } else {
      next.delete(projectNodeId);
    }
    this.selectedNodeIds.set(next);
  }

  toggleAll(checked: boolean): void {
    this.selectedNodeIds.set(new Set(checked ? (this.preview()?.matches ?? []).map(m => m.projectNodeId) : []));
  }

  link(): void {
    const template = this.selectedTemplate();
    if (!template || !this.canLink()) {
      return;
    }
    // Linking every match is sent without a body; a subset is sent as an explicit list.
    const ids = this.allSelected() ? undefined : [...this.selectedNodeIds()];
    this.saving.set(true);
    this.service
      .link(template.id, this.data.projectId, ids)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.saving.set(false);
          this.dialogRef.close({ templateId: template.id, templateName: template.name, linked: result.linked });
        },
        error: err => {
          this.saving.set(false);
          this.showError(err);
        },
      });
  }

  private showError(err: unknown): void {
    const m = mapTemplateError(err);
    this.snackBar.error(this.translate.instant(m.key, m.params));
  }
}
